import { useState, useEffect, Fragment } from 'react'
import { evaluationAPI } from '../../services/api'

const DIMENSION_LABELS = {
  clinical_domain_coverage: 'Clinical Domain Coverage',
  guideline_adherence: 'Guideline Adherence',
  red_flag_escalation: 'Red Flag Escalation',
  documentation_quality: 'Documentation Quality',
  shared_decision_making: 'Shared Decision Making',
  clinical_questioning: 'Clinical Questioning',
  follow_up_planning: 'Follow-up Planning',
  persona_fidelity: 'Persona Fidelity',
  coherence_and_context: 'Coherence & Context',
  naturalness: 'Naturalness',
}

const TIER_ORDER = [
  { tier: 'Clinical Validity', weight: '2.0x', dims: ['clinical_domain_coverage', 'guideline_adherence', 'red_flag_escalation'] },
  { tier: 'Encounter Quality', weight: '1.5x', dims: ['documentation_quality', 'shared_decision_making', 'clinical_questioning', 'follow_up_planning'] },
  { tier: 'Conversation Realism', weight: '1.0x', dims: ['persona_fidelity', 'coherence_and_context', 'naturalness'] },
]

const PROVIDER_LABELS = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Gemini',
}

const PROVIDER_MODELS = {
  openai: 'o4-mini',
  anthropic: 'claude-sonnet-4-6',
  gemini: 'gemini-3.1-pro-preview',
}

function ScoreCell({ score, bold }) {
  if (score === null || score === undefined) return <td className="text-center text-base-content/30">-</td>
  const color = score >= 4 ? 'text-success' : score >= 3 ? 'text-warning' : 'text-error'
  return (
    <td className={`text-center font-mono ${color} ${bold ? 'font-bold' : 'font-medium'}`}>
      {score.toFixed(2)}
    </td>
  )
}

function ScoreBadge({ score }) {
  if (score === null || score === undefined) return <span className="text-base-content/30">-</span>
  const color = score >= 4 ? 'badge-success' : score >= 3 ? 'badge-warning' : 'badge-error'
  return <span className={`badge ${color} badge-sm font-mono`}>{score.toFixed(1)}</span>
}

function SpreadCell({ spread }) {
  if (spread === null || spread === undefined) return <td className="text-center text-base-content/30">-</td>
  const color = spread > 1.0 ? 'text-error font-semibold' : 'text-base-content/50'
  return <td className={`text-center font-mono text-sm ${color}`}>{spread.toFixed(2)}</td>
}

function AverageCell({ scores, bold }) {
  if (!scores || scores.length === 0) return <td className="text-center text-base-content/30">-</td>
  const valid = scores.filter(s => s !== null && s !== undefined && s > 0)
  if (valid.length === 0) return <td className="text-center text-base-content/30">-</td>
  const avg = valid.reduce((a, b) => a + b, 0) / valid.length
  const color = avg >= 4 ? 'text-success' : avg >= 3 ? 'text-warning' : 'text-error'
  return (
    <td className={`text-center font-mono ${color} ${bold ? 'font-bold' : 'font-medium'}`}>
      {avg.toFixed(2)}
    </td>
  )
}

export default function MultiJudgePanel({ journeyId, onEvaluationComplete }) {
  const [results, setResults] = useState(null)
  const [availableProviders, setAvailableProviders] = useState([])
  const [selectedProviders, setSelectedProviders] = useState(new Set(['openai', 'anthropic', 'gemini']))
  const [running, setRunning] = useState(false)
  const [error, setError] = useState(null)
  const [activeModelTab, setActiveModelTab] = useState(null)

  useEffect(() => {
    evaluationAPI.getAvailableProviders()
      .then(data => {
        setAvailableProviders(data.providers || [])
        setSelectedProviders(new Set(data.providers || []))
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!journeyId) return
    evaluationAPI.getMultiJudgeComparison(journeyId)
      .then(data => {
        if (data && data.providers) {
          setResults(parseStoredResults(data))
        }
      })
      .catch(() => {})
  }, [journeyId])

  const parseStoredResults = (data) => {
    const providers = data.providers || {}
    const providerNames = Object.keys(providers)
    if (providerNames.length === 0) return null

    const allDims = Object.keys(DIMENSION_LABELS)
    const tierWeights = {}
    for (const { dims, weight } of TIER_ORDER) {
      const w = parseFloat(weight)
      for (const d of dims) tierWeights[d] = w
    }

    const providerRollups = {}
    for (const [pname, metrics] of Object.entries(providers)) {
      // First check for pre-computed rollup metrics
      const rollups = {}
      let overallScore = 0
      let hasRollups = false

      for (const m of metrics) {
        if (m.metric_name.endsWith('_rollup')) {
          const dim = m.metric_name.replace('llm_judge_', '').replace('_rollup', '')
          rollups[dim] = { mean: m.raw_score }
          hasRollups = true
        }
        if (m.metric_name === 'llm_judge_overall') {
          overallScore = m.raw_score
        }
      }

      // If no rollup metrics found, compute from per-episode data
      if (!hasRollups) {
        const dimScores = {}
        const episodeResults = []
        const episodeMap = {}

        for (const m of metrics) {
          const details = m.calculation_details || {}
          if (!details.episode_number) continue
          const dimKey = m.metric_name.replace('llm_judge_', '')
          if (!DIMENSION_LABELS[dimKey]) continue

          if (!dimScores[dimKey]) dimScores[dimKey] = []
          dimScores[dimKey].push(m.raw_score)

          // Build episode_results for model breakdown
          const epNum = details.episode_number
          if (!episodeMap[epNum]) {
            episodeMap[epNum] = { episode_number: epNum, scores: {}, rationales: {} }
          }
          episodeMap[epNum].scores[dimKey] = m.raw_score
          episodeMap[epNum].rationales[dimKey] = details.rationale || ''
        }

        for (const dim of allDims) {
          const scores = dimScores[dim] || []
          if (scores.length > 0) {
            const mean = scores.reduce((a, b) => a + b, 0) / scores.length
            rollups[dim] = {
              mean: Math.round(mean * 100) / 100,
              min: Math.min(...scores),
              max: Math.max(...scores),
            }
          }
        }

        // Compute weighted overall
        let weightedSum = 0, totalWeight = 0
        for (const dim of allDims) {
          if (rollups[dim]) {
            const w = tierWeights[dim] || 1.0
            weightedSum += rollups[dim].mean * w
            totalWeight += w
          }
        }
        overallScore = totalWeight > 0 ? Math.round((weightedSum / totalWeight) * 100) / 100 : 0

        // Attach episode_results and model info for the breakdown section
        const firstMetric = metrics.find(m => m.calculation_details?.judge_model)
        providerRollups[pname] = {
          dimension_rollups: rollups,
          overall_score: overallScore,
          episode_results: Object.values(episodeMap).sort((a, b) => a.episode_number - b.episode_number),
          model: firstMetric?.calculation_details?.judge_model || PROVIDER_MODELS[pname] || 'unknown',
        }
        continue
      }

      providerRollups[pname] = { dimension_rollups: rollups, overall_score: overallScore }
    }

    const comparison = {}
    for (const dim of allDims) {
      const scores = {}
      for (const pname of providerNames) {
        scores[pname] = providerRollups[pname]?.dimension_rollups?.[dim]?.mean || 0
      }
      const values = Object.values(scores)
      scores.spread = values.length > 0 ? Math.max(...values) - Math.min(...values) : 0
      comparison[dim] = scores
    }

    const overallScores = {}
    for (const pname of providerNames) {
      overallScores[pname] = providerRollups[pname]?.overall_score || 0
    }
    const overallValues = Object.values(overallScores)
    overallScores.spread = overallValues.length > 0 ? Math.max(...overallValues) - Math.min(...overallValues) : 0
    comparison._overall = overallScores

    return { providers: providerRollups, comparison, providerNames }
  }

  const toggleProvider = (name) => {
    setSelectedProviders(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const runMultiJudge = async () => {
    const providers = [...selectedProviders].filter(p => availableProviders.includes(p))
    if (providers.length === 0) {
      setError('Select at least one available provider')
      return
    }
    setRunning(true)
    setError(null)
    try {
      const data = await evaluationAPI.multiJudgeEvaluate(journeyId, providers)
      const providerNames = Object.keys(data.providers || {})
      setResults({
        providers: data.providers,
        comparison: data.comparison,
        providerNames,
        episodesEvaluated: data.episodes_evaluated,
        totalEpisodes: data.total_episodes,
        rubricVersion: data.rubric_version,
      })
      if (providerNames.length > 0 && !activeModelTab) {
        setActiveModelTab(providerNames[0])
      }
      if (onEvaluationComplete) onEvaluationComplete()
    } catch (err) {
      setError(err.response?.data?.detail || 'Multi-judge evaluation failed')
    } finally {
      setRunning(false)
    }
  }

  const providerNames = results?.providerNames || []

  // Build per-model dimension rationales aggregated across episodes
  const getModelBreakdown = (pname) => {
    const providerData = results?.providers?.[pname]
    if (!providerData) return null

    const episodeResults = providerData.episode_results || []
    const rollups = providerData.dimension_rollups || {}

    // Aggregate rationales per dimension across episodes
    const dimBreakdown = {}
    for (const dim of Object.keys(DIMENSION_LABELS)) {
      const episodeRationales = []
      for (const ep of episodeResults) {
        const rationale = ep.rationales?.[dim]
        const score = ep.scores?.[dim]
        if (rationale || score !== undefined) {
          episodeRationales.push({
            episode: ep.episode_number,
            score,
            rationale: rationale || '',
          })
        }
      }
      dimBreakdown[dim] = {
        mean: rollups[dim]?.mean || 0,
        min: rollups[dim]?.min,
        max: rollups[dim]?.max,
        episodes: episodeRationales,
      }
    }

    return {
      overall: providerData.overall_score,
      model: providerData.model || PROVIDER_MODELS[pname] || 'unknown',
      totalTokens: providerData.total_tokens || 0,
      breakdown: dimBreakdown,
    }
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="card-title text-lg">Multi-Model LLM Judge</h3>
          <button
            className={`btn btn-sm ${results ? 'btn-outline' : 'btn-secondary'}`}
            onClick={runMultiJudge}
            disabled={running}
          >
            {running ? (
              <><span className="loading loading-spinner loading-xs"></span> Running...</>
            ) : results ? 'Re-run' : 'Run Multi-Judge'}
          </button>
        </div>

        {/* Provider checkboxes */}
        <div className="flex flex-wrap gap-3 mt-1">
          {['openai', 'anthropic', 'gemini'].map(name => {
            const available = availableProviders.includes(name)
            const selected = selectedProviders.has(name)
            return (
              <label key={name} className={`flex items-center gap-1.5 cursor-pointer ${!available ? 'opacity-40' : ''}`}>
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm checkbox-secondary"
                  checked={selected}
                  onChange={() => toggleProvider(name)}
                  disabled={!available || running}
                />
                <span className="text-sm">{PROVIDER_LABELS[name]}</span>
                {!available && <span className="text-xs text-base-content/40">(no key)</span>}
              </label>
            )
          })}
        </div>

        {error && (
          <div className="alert alert-error mt-2">
            <span>{error}</span>
          </div>
        )}

        {running && (
          <div className="mt-3">
            <div className="flex items-center gap-2 text-sm text-base-content/60">
              <span className="loading loading-dots loading-sm"></span>
              Running evaluation across {[...selectedProviders].filter(p => availableProviders.includes(p)).length} providers... This may take a few minutes.
            </div>
          </div>
        )}

        {results && !running && (
          <div className="mt-2 space-y-5">
            {/* Metadata */}
            {results.episodesEvaluated && (
              <div className="text-xs text-base-content/50">
                {results.episodesEvaluated}/{results.totalEpisodes} episodes evaluated
                {results.rubricVersion && ` | Rubric v${results.rubricVersion}`}
              </div>
            )}

            {/* Comparison table */}
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr className="border-b border-base-300">
                    <th className="text-xs font-semibold">Dimension</th>
                    {providerNames.map(p => (
                      <th key={p} className="text-center text-xs font-semibold">{PROVIDER_LABELS[p] || p}</th>
                    ))}
                    {providerNames.length > 1 && (
                      <th className="text-center text-xs font-semibold">Average</th>
                    )}
                    {providerNames.length > 1 && (
                      <th className="text-center text-xs font-semibold">Spread</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {TIER_ORDER.map(({ tier, weight, dims }) => (
                    <Fragment key={tier}>
                      <tr className="bg-base-200/60">
                        <td
                          colSpan={providerNames.length + (providerNames.length > 1 ? 3 : 1)}
                          className="font-bold text-xs uppercase tracking-wider py-1.5 text-base-content/70"
                        >
                          {tier} <span className="font-normal text-base-content/40">({weight})</span>
                        </td>
                      </tr>
                      {dims.map(dim => {
                        const row = results.comparison?.[dim] || {}
                        const providerScores = providerNames.map(p => row[p])
                        return (
                          <tr key={dim} className="hover:bg-base-200/30">
                            <td className="text-sm py-1.5">{DIMENSION_LABELS[dim] || dim}</td>
                            {providerNames.map(p => (
                              <ScoreCell key={p} score={row[p]} />
                            ))}
                            {providerNames.length > 1 && (
                              <AverageCell scores={providerScores} />
                            )}
                            {providerNames.length > 1 && (
                              <SpreadCell spread={row.spread} />
                            )}
                          </tr>
                        )
                      })}
                    </Fragment>
                  ))}
                  <tr className="border-t-2 border-base-300 bg-base-200/30">
                    <td className="font-bold text-sm py-2">Overall (weighted)</td>
                    {providerNames.map(p => (
                      <ScoreCell key={p} score={results.comparison?._overall?.[p]} bold />
                    ))}
                    {providerNames.length > 1 && (
                      <AverageCell
                        scores={providerNames.map(p => results.comparison?._overall?.[p])}
                        bold
                      />
                    )}
                    {providerNames.length > 1 && (
                      <SpreadCell spread={results.comparison?._overall?.spread} />
                    )}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Per-model grading breakdown */}
            <div>
              <h4 className="text-sm font-bold mb-2">Model Grading Breakdown</h4>
              <p className="text-xs text-base-content/50 mb-3">
                How and why each model scored each dimension, with rationales aggregated across episodes.
              </p>

              {/* Model tabs */}
              <div className="tabs tabs-boxed bg-base-200 mb-3">
                {providerNames.map(pname => (
                  <button
                    key={pname}
                    className={`tab tab-sm ${(activeModelTab || providerNames[0]) === pname ? 'tab-active' : ''}`}
                    onClick={() => setActiveModelTab(pname)}
                  >
                    {PROVIDER_LABELS[pname] || pname}
                  </button>
                ))}
              </div>

              {/* Active model breakdown */}
              {(() => {
                const pname = activeModelTab || providerNames[0]
                const breakdown = getModelBreakdown(pname)
                if (!breakdown) return null

                return (
                  <div className="space-y-1">
                    {/* Model header */}
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-sm font-semibold">{PROVIDER_LABELS[pname]}</span>
                      <span className="text-xs text-base-content/50">{breakdown.model}</span>
                      <span className={`badge badge-lg font-bold ${breakdown.overall >= 4 ? 'badge-success' : breakdown.overall >= 3 ? 'badge-warning' : 'badge-error'}`}>
                        {breakdown.overall?.toFixed(2)} / 5.00
                      </span>
                    </div>

                    {/* Dimensions grouped by tier */}
                    {TIER_ORDER.map(({ tier, dims }) => (
                      <div key={tier} className="mb-3">
                        <div className="text-xs font-bold uppercase tracking-wider text-base-content/50 mb-1.5 px-1">
                          {tier}
                        </div>
                        <div className="space-y-1.5">
                          {dims.map(dim => {
                            const dimData = breakdown.breakdown[dim]
                            if (!dimData) return null
                            // Pick a representative rationale (first non-empty one, or most recent)
                            const topRationales = dimData.episodes
                              .filter(e => e.rationale)
                              .slice(0, 2)

                            return (
                              <div key={dim} className="bg-base-200/50 rounded-lg px-3 py-2">
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="text-sm font-medium">{DIMENSION_LABELS[dim]}</span>
                                  <div className="flex items-center gap-2">
                                    <ScoreBadge score={dimData.mean} />
                                    {dimData.min !== undefined && dimData.max !== undefined && dimData.min !== dimData.max && (
                                      <span className="text-xs text-base-content/40">
                                        ({dimData.min}-{dimData.max})
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {topRationales.length > 0 && (
                                  <div className="text-xs text-base-content/60 leading-relaxed mt-1">
                                    {topRationales.map((r, i) => (
                                      <span key={i}>
                                        {topRationales.length > 1 && (
                                          <span className="font-medium text-base-content/40">Ep {r.episode}: </span>
                                        )}
                                        {r.rationale}
                                        {i < topRationales.length - 1 && ' | '}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })()}
            </div>
          </div>
        )}

        {!results && !running && !error && (
          <p className="text-sm text-base-content/50 mt-1">
            Run the same rubric across multiple LLM judges to compare scoring consistency.
          </p>
        )}
      </div>
    </div>
  )
}
